import cn from 'classnames';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import RightNav from 'organisms/Navigation/RightNav';
import React, { useEffect, useMemo } from 'react';
import { useStore } from 'stores/store';
import { reportEvent } from 'utils/analytics';
import { IAuthDetails, navigateToSignOut, signOut } from 'utils/authUtils';
import { IHeaderConfig, INavItem } from 'utils/globalTypes';
import { useUserAuth } from 'utils/hooks/useUserAuth';
import { navigateToUrl } from 'utils/navigateUtils';
import { sameParent, sameUrl } from 'utils/urlUtils';
import { connectWS } from 'utils/webSocketUtils';

import LogoNav from './LogoNav';
import MainNav from './MainNav';
import s from './Navigation.module.scss';

interface INavigation {
  navigationBarItems: Array<INavItem>;
  burgerMenuItems: Array<INavItem>;
  sideBarItems: Array<INavItem>;
  headerConfig?: IHeaderConfig;
  authDetails?: IAuthDetails;
  isSticky?: boolean;
  isBottom?: boolean;
}

const Navigation = ({
  navigationBarItems,
  burgerMenuItems,
  sideBarItems,
  headerConfig,
  authDetails,
  isSticky,
  isBottom
}: INavigation) => {
  const store = useStore();
  const router = useRouter();

  const clientAuthDetails = useUserAuth();

  const initializeWS = useMemo(
    () =>
      debounce(() => {
        connectWS(clientAuthDetails);
      }, 1000),
    [clientAuthDetails]
  );

  useEffect(() => {
    initializeWS();
  }, [initializeWS]);

  if (router?.pathname?.startsWith('/connect')) {
    return null;
  }

  const sideBar = !!(
    headerConfig?.sideBar || sideBarItems?.filter(({ url }) => sameParent(router, url)).length
  );

  const transparent = !!(
    headerConfig?.transparent ||
    navigationBarItems.filter(({ transparent, url }) => transparent && sameUrl(router, url))
      .length ||
    sameUrl(router, '/')
  );

  const dark = !!(
    headerConfig?.dark ||
    navigationBarItems.filter(({ dark, url }) => dark && sameUrl(router, url)).length ||
    sameUrl(router, '/')
  );

  const narrow = !!(
    headerConfig?.narrow ||
    navigationBarItems.filter(({ narrow, url }) => narrow && sameUrl(router, url)).length ||
    sameUrl(router, '/')
  );

  const onAuthChange = async (state: string) => {
    return navigateToUrl(`/connect/${state}`);
  };

  const onAction = async (actionName: string, url?: string, newTab?: boolean) => {
    reportEvent(actionName, {
      url: url
    });

    switch (actionName) {
      case 'signOut':
        return navigateToSignOut();
      case 'open_sign_in':
        return onAuthChange('signIn');
      case 'open_sign_up':
        return onAuthChange('signUp');
      case 'notifications':
        return store.UIStore.setNotificationsActive();
    }

    switch (url) {
      case 'signOut':
        return navigateToSignOut();
      case 'open_sign_in':
        return onAuthChange('signIn');
      case 'open_sign_up':
        return onAuthChange('signUp');
      case 'notifications':
        return store.UIStore.setNotificationsActive();
    }

    navigateToUrl(url, { newTab });
  };

  return (
    <>
      <div
        className={cn(s.Navigation, {
          transparent,
          dark,
          narrow: typeof headerConfig?.narrow === 'boolean' ? headerConfig?.narrow : narrow,
          isSticky,
          isBottom,
          sideBar
        })}
      >
        <>
          <LogoNav transparent={transparent} dark={dark} sideBar={sideBar} isSticky={isSticky} />
          <MainNav
            transparent={transparent}
            dark={dark}
            isSticky={isSticky}
            navItems={navigationBarItems}
            onAction={onAction}
          />
          <RightNav
            isImpersonatedMode={authDetails?.isImpersonated}
            transparent={transparent}
            dark={dark}
            isSticky={isSticky}
            burgerMenuItems={burgerMenuItems}
            onAction={onAction}
          />
        </>
      </div>
    </>
  );
};

export default observer(Navigation);
